import { useCallback, useEffect, useState } from "react";
import { Button, Modal } from "@monolith-forensics/monolith-ui";
import { useSnackbar } from "notistack";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import type { File } from "@/Features/Files/files.types";
import { useDeleteEvidencePhoto } from "@/Features/EvidencePhotos";
import { EvidencePhotoModalAlert } from "./EvidencePhotoModalAlert";

export const DeleteEvidencePhotoModal: React.FC<{
  show: boolean;
  evidenceId: number;
  file: File;
  onClose?: () => void;
  onSubmit?: () => void;
}> = ({ show, evidenceId, file, onClose, onSubmit }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { mutate: deleteEvidencePhoto } = useDeleteEvidencePhoto();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = () => {
    setSubmitting(true);
    deleteEvidencePhoto(
      { evidence_id: evidenceId, photo_uuid: file.uuid },
      {
        onSuccess: () => {
          setSubmitting(false);
          enqueueSnackbar("Photo deleted.", { variant: "success" });
          onSubmit?.();
        },
        // The data layer rejects { success: false }; the axios interceptor
        // already toasted the HTTP error.
        onError: (error) => {
          setSubmitting(false);
          console.error(error);
        },
      }
    );
  };

  const handleCancel = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [submitting, onClose]);

  // The base Modal does not dismiss on escape.
  useEffect(() => {
    if (!show) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleCancel();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, handleCancel]);

  return (
    <Modal open={show} onClose={handleCancel} showCloseButton={true}>
      <Modal.Title>Delete Photo</Modal.Title>
      <EvidencePhotoModalAlert kind="delete" />
      <div style={{ fontWeight: 600, margin: "20px 0px", width: "350px" }}>
        Are you sure you want to delete this photo? This can't be undone.
      </div>
      <div style={{ fontWeight: 600, marginBottom: "20px", width: "350px" }}>
        "{file.name}"
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          color="error"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          Delete
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
